import React from 'react';
import { styled } from '@mui/material/styles';
import ListSubheader from '@mui/material/ListSubheader';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Popover, { PopoverProps } from '@mui/material/Popover';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Box from '@mui/material/Box';
import Button from '../button';

type OmittedMenuProps = Omit<PopoverProps, 'open' | 'title'>

interface Props extends OmittedMenuProps {
  readonly title?: string;
  readonly titleSmall?: boolean;
  readonly width?: number;
  readonly maxWidth?: number;
  readonly onPositive?: () => void;
  readonly onNegative?: () => void;
  readonly noPadding?: boolean;
  readonly noActions?: boolean;
  readonly noTransform?: boolean;
  readonly positive?: string;
  readonly positiveDisabled?: boolean;
  readonly negative?: string;
  readonly removeNegativeOption?: boolean;
  readonly negativeDisabled?: boolean;
  readonly renderFooter?: () => React.ReactNode;
  readonly overflowVisible?: boolean;
}

const MenuDialogContent = styled(DialogContent)({
  padding: 24,
  overflowY: 'hidden',
});

const MenuDialogActions = styled(DialogActions)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  padding: '8px 0',
});

export default function MenuModal({ ...props }: Props) {
  return (
    <Popover
      anchorEl={props.anchorEl}
      open={Boolean(props.anchorEl)}
      onClose={props.onClose}
      sx={{ mt: 1, overflow: 'none' }}
      {...props.anchorOrigin ? {
        anchorOrigin: props.anchorOrigin,
      } : {
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right',
        },
      }}
      {...!props.noTransform ? {
        transformOrigin: {
          vertical: 'top',
          horizontal: 'right',
        },
      } : {}}
      {...props.overflowVisible ? {
        PaperProps: {
          sx: {
            overflow: 'visible',
          },
        },
      } : {}}
    >
      {!!props.title && !props.titleSmall && <DialogTitle>{props.title}</DialogTitle>}
      {props.titleSmall && (
        <ListSubheader sx={{ p: 0 }}>
          <Stack direction="row" sx={{ pl: 3 }}>
            <Typography variant="overline" color="gray.dark">
              {props.title}
            </Typography>
          </Stack>
          <Divider />
        </ListSubheader>
      )}
      <MenuDialogContent
        sx={{
          height: 'auto',
          display: 'flex',
          flexDirection: 'column',
          ...(props.noPadding && {
            padding: 0,
          }),
          ...(props.width && {
            width: props.width,
          }),
          ...(props.maxWidth && {
            maxWidth: props.maxWidth,
          }),
        }}
        {...(!props.noActions && {
          dividers: true,
        })}
      >
        {props.children}
      </MenuDialogContent>
      {!props.noActions && (
        <MenuDialogActions
          {...!props.renderFooter ? {
            sx: { justifyContent: 'flex-end' },
          } : {}}
        >
          {props.renderFooter && (
            <Box>{props.renderFooter()}</Box>
          )}
          <Box pr={1}>
            {!props.removeNegativeOption && (
              <Button
                onClick={props.onNegative}
                disabled={props.negativeDisabled}
              >
                {props.negative ?? 'Cancel'}
              </Button>
            )}
            {props.positive && (
              <Button
                color="secondary"
                type="button"
                disabled={props.positiveDisabled}
                onClick={props.onPositive}
              >
                {props.positive}
              </Button>
            )}
          </Box>
        </MenuDialogActions>
      )}
    </Popover>
  );
}
